import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../firebase/firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/firebase';

// Extend User type to include role
interface ExtendedUser extends User {
  role?: 'student' | 'teacher' | 'pending_teacher';
}

// Define the shape of our context
interface AuthContextType {
  currentUser: ExtendedUser | null;
  loading: boolean;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({ currentUser: null, loading: true });

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Provider component that wraps your app and makes auth object available to any child component that calls useAuth()
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<ExtendedUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Load role from Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            // Attach role to user object
            const extendedUser = user as ExtendedUser;
            extendedUser.role = userData.role || 'student';
            setCurrentUser(extendedUser);
          } else {
            setCurrentUser(user as ExtendedUser);
          }
        } catch (error) {
          console.error('Error loading user role:', error);
          setCurrentUser(user as ExtendedUser);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;